import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, updateDoc, deleteDoc, setDoc, Timestamp, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase/config';
import { getFeatures } from '../utils/features';
import { createNotification } from '../services/NotificationSystem';
import { findPriceMatch, normalizePriceItems } from '../utils/statementItems';
import FlowBar from './common/FlowBar';
import { ArrowLeft, Edit2, Save, X, Printer, Trash2, Plus, MessageSquare, CheckCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getStatementDeleteErrorMessage } from '../utils/statementDeleteError';
import { isAdminUser } from '../utils/adminClaim';
// 양식·계산·인쇄 공용화 (2026-10-06 미리보기 A안): 양식 마크업은 StatementSheet, 계산·CSS는 utils/statementView, 인쇄는 utils/printStatement
import StatementSheet from './StatementSheet';
import { fillMfItemsFromOrders } from '../utils/mfImplantPrice';   // 외주 명세서 보기 보강 (2026-11-02)
import {
    parseToothCount, normalizeStatementItems, computeStatementTotals,
    lastDisputeNoteOf, disputedIndexMapOf, statementItemKeyOf, STATEMENT_LEDGER_CSS,
} from '../utils/statementView';
import { printStatement, statementPrintTitle } from '../utils/printStatement';
import { deleteDraftAndRestoreCases, markDeletedAndRestoreCases } from '../utils/caseRestore';   // 삭제 시 케이스 되돌림 (2026-10-23 1단계)
import { loadReceivables, statementPaymentDetail, invalidateReceivables, computeCarryOver } from '../utils/receivables';
import { invalidateSummaryCache } from './statement/SummaryCards';
import { partnerKeyOfStatement, isMemberPartnerKey, toDateAny } from '../utils/billingCycle';
import { isLabStaff } from '../utils/excelExport';
import { paymentStateInfo, PAYMENT_STATE_STYLE, isLedgerStatement } from './statement/paymentState';

// 외주 명세서 종류 — TransactionStatementList.js 의 OUTSOURCE_KINDS 와 같은 목록 (2026-10-23 검토 회의)
const OUTSOURCE_KINDS = ['design_outsource', 'outsource_mf'];
// 세금계산서 '취소류' 상태 — 삭제·정정 가드 공통 기준
const CANCELED_LIKE_TAX = ['canceled', 'cancelled', 'voided'];
// 정산 확인 버튼·확인창 법무 고정 문구 (계약 §2, 2026-10-23) — 글자 바꾸지 말 것
const SETTLE_CARRY_OVER_NOTE = '정산 확인은 이번 명세서 합계에 대한 확인이며, 전월 미수 안내는 확인 대상에 포함되지 않습니다.';
// 원클릭 확인창(비회원) 법무 고정 문구 (계약 §2, 2026-10-24) — 글자 바꾸지 말 것
const ONECLICK_NONMEMBER_NOTE = '치과의 링크 확인은 정산 확정이 아닙니다. 계산서 발행 여부와 내용은 기공소가 판단합니다.';
// 팝빌 잠금(popbillLive=false) 중 원클릭 안내 (계약 §1)
const ONECLICK_LOCKED_NOTE = '전자발행은 준비 중 — 덴클 기록만 남깁니다';
// 서버 함수 (asia-northeast3 — 기존 callable 관례)
const callFn = (name) => httpsCallable(getFunctions(undefined, 'asia-northeast3'), name);
const mdOf = (v) => { const d = toDateAny(v); return d ? `${d.getMonth() + 1}/${d.getDate()}` : ''; };
const msOf = (v) => toDateAny(v)?.getTime() || 0;
// 비회원 치과 — 서버 원클릭(functions/lib/invoice-oneclick.js NON_MEMBER_ID·isManual)과 같은 기준. 최종 판정은 서버
const isNonMemberStatement = (st) => {
    const id = String(st?.toUserId || '');
    return !id || /^(agent_|email_|order_|name:)/.test(id) || id.includes('/') || st?.isManual === true;
};
// 알림 recipientId 가드 — 비회원 이름 키(email_/agent_/order_/name:)에는 알림을 만들지 않는다 (작성 화면과 같은 기준, 2026-10-24 재점검 P2-12)
const isGhostRecipientId = (id) => !id || /^(agent_|email_|order_|name:)/.test(String(id));
// 원클릭 잠금(pending:) 이 이 시간을 넘으면 '멈춤'으로 보고 [다시 시도] (서버도 같은 건을 이어서 처리)
const ONECLICK_STALL_MS = 10 * 60 * 1000;
// 공유 링크 수정 요청 칸 rev1~rev3 (rules 계약 §3)
const shareRevisionsOf = (sh) => [1, 2, 3].map(n => sh?.[`rev${n}`]).filter(r => r && typeof r.text === 'string');

// readOnly: 결제 전 기공소(제한 모드) — 보기·인쇄만, 수정·발행·공유 링크·삭제·정산 확인·계산서 발행 숨김 (계획_가입승인결제흐름 W6, 법무 R4)
// onCorrect(statementId): 정산 완료본 [고쳐서 다시 발행] — 목록이 작성 화면을 correctFromId로 연다 (2026-10-23)
function ViewTransactionStatement({ statementId, user, onBack, onEdit, onCorrect, readOnly = false }) {
    // ⋯ 보조 메뉴 (인쇄·삭제 등)
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const navigate = useNavigate();
    const [statement, setStatement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);   // 조회 오류(문서 없음과 구분) — 조용한 실패 금지 (2026-10-23)
    const [editing, setEditing] = useState(false);
    const [editedItems, setEditedItems] = useState([]);
    const [editedDiscount, setEditedDiscount] = useState(0);
    const [editedDiscountType, setEditedDiscountType] = useState('amount');
    const [editedNotes, setEditedNotes] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);   // 더블클릭 중복 저장·알림 방지
    const [publishing, setPublishing] = useState(false);

    // 수신자 검토 모드 (체크박스 + 사유 입력)
    const [reviewing, setReviewing] = useState(false);
    const [checkedItems, setCheckedItems] = useState({});
    const [reviewReasons, setReviewReasons] = useState({});
    const [reviewGeneralNote, setReviewGeneralNote] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);

    // 단가표
    const [priceList, setPriceList] = useState([]);
    const [showPriceListModal, setShowPriceListModal] = useState(false);
    const [editingPriceList, setEditingPriceList] = useState([]);

    // 프리셋
    const DEFAULT_PROSTHESIS = ['Crown', 'Inlay', 'POST', 'Denture', '교정', '장치'];
    const DEFAULT_IMPLANTS = ['Osstem TS Mini', 'Osstem TS Regular', 'Osstem TS Wide', 'Osstem KS'];
    const [prosthesisPresets, setProsthesisPresets] = useState(DEFAULT_PROSTHESIS);
    const [implantPresets, setImplantPresets] = useState(DEFAULT_IMPLANTS);

    // 마감 진행줄: 덴클 매칭 여부 (수수료 4.5% 대상 판정 — partnerships/{clinicId_labId})
    const [matchedPartnership, setMatchedPartnership] = useState(null); // null=조회중, false=자체거래처, {..}=매칭

    const targetId = user?.companyId || user?.uid;
    const isCreator = statement?.fromUserId === user?.uid || statement?.fromUserId === targetId;

    useEffect(() => {
        (async () => {
            if (!statement?.fromUserId || !statement?.toUserId) return;
            try {
                const pSnap = await getDoc(doc(db, 'partnerships', `${statement.toUserId}_${statement.fromUserId}`));
                const pd = pSnap.exists() ? pSnap.data() : null;
                setMatchedPartnership(pd && pd.status === 'active' && pd.commissionStartDate ? pd : false);
            } catch { setMatchedPartnership(false); }
        })();
    }, [statement?.fromUserId, statement?.toUserId]);
    const isReceiver = !isCreator && (statement?.toUserId === user?.uid || statement?.toUserId === targetId);
    const isDraft = statement?.status === 'draft';
    // 정산 완료본 잠금 (2026-10-23 대표 확정) — 금액·항목 수정 불가, 틀리면 [고쳐서 다시 발행]으로 새 명세서
    const isSettled = statement?.settlementStatus === 'settled';
    const canEdit = isCreator && !readOnly && !isSettled;
    // 정산 완료본은 삭제 대신 [고쳐서 다시 발행]으로 정정 (2026-10-23 검토 회의 — 규칙도 correctedTo 없는 삭제 표시를 막음)
    const canDelete = isCreator && !readOnly && !isSettled;
    // 발행처가 삭제한 명세서 — 수신자는 정산 등 액션 불가, 안내만 표시 (2026-08-26)
    const isDeletedBySender = !!statement?.deletedBySender;
    // 정정됨 — 발행처가 [고쳐서 다시 발행]으로 새 명세서를 발행해 이 명세서를 대체 (2026-10-23)
    const isCorrected = isDeletedBySender && !!statement?.correctedTo;
    // 외주 명세서는 제외 — 규칙상 외주 원본엔 '정정됨' 표시를 못 해 두 장이 모두 청구됨 (2026-10-23 검토 회의)
    const isOutsourceStatement = OUTSOURCE_KINDS.includes(statement?.kind);
    const canCorrect = isCreator && !readOnly && isSettled && !isDeletedBySender && !isOutsourceStatement;

    // 정정본은 발행됐는데 원본 '정정됨' 표시가 실패한 경우 — 원본을 열면 정정본을 찾아 다시 시도하게 한다 (2026-10-23)
    const [pendingCorrection, setPendingCorrection] = useState(null);   // { id, statementNumber } | null
    const [retryingCorrected, setRetryingCorrected] = useState(false);
    useEffect(() => {
        setPendingCorrection(null);
        if (!statement?.id || !isCreator || readOnly || !isSettled || isDeletedBySender || !statement?.fromUserId) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(query(
                    collection(db, 'transactionStatements'),
                    where('fromUserId', '==', statement.fromUserId),
                    where('correctsId', '==', statementId),
                    where('status', '==', 'confirmed'),
                ));
                const live = snap.docs.filter(d => !d.data().deletedBySender);
                if (!cancelled && live.length > 0) {
                    const d = live[0];
                    setPendingCorrection({ id: d.id, statementNumber: d.data().statementNumber || '' });
                }
            } catch (e) {
                console.error('정정본 조회 실패:', e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statement?.id, statement?.fromUserId, isCreator, readOnly, isSettled, isDeletedBySender, statementId]);

    // 입금 표시 (2026-10-23 1단계 §5-3) — 기공소 대표만(직원·치과 X), 받을 돈은 PIN 구역(/transactions) 화면 안에서만. 정산 상태와 섞지 않는다
    const isLabOwnerView = isCreator && !readOnly && !isLabStaff(user);
    const [paymentInfo, setPaymentInfo] = useState(null);   // paymentStateInfo 결과 | null
    useEffect(() => {
        setPaymentInfo(null);
        if (!isLabOwnerView || !isLedgerStatement(statement)) return;
        let cancelled = false;
        (async () => {
            try {
                const rec = await loadReceivables(statement.fromUserId || targetId);
                if (cancelled) return;
                if (rec?.error) console.warn('입금 표시용 원장 일부 실패:', statementId, rec.error?.code || rec.error?.message || rec.error);
                const entry = rec?.byPartner?.[partnerKeyOfStatement(statement)];
                if (!entry) return;
                setPaymentInfo(paymentStateInfo(statementPaymentDetail({ id: statementId, ...statement }, entry)));
            } catch (e) {
                console.error('입금 표시 계산 실패:', statementId, e?.code || e?.message);   // id만
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statement?.id, statement?.status, statement?.deletedBySender, statement?.totalAmount, isLabOwnerView]);

    const handleRetryMarkCorrected = async () => {
        if (!pendingCorrection?.id || retryingCorrected) return;
        setRetryingCorrected(true);
        try {
            await updateDoc(doc(db, 'transactionStatements', statementId), {
                deletedBySender: true,
                deletedBySenderAt: serverTimestamp(),
                correctedTo: pendingCorrection.id,
            });
            setPendingCorrection(null);
            invalidateReceivables(statement?.fromUserId || targetId);   // 정정 원본이 원장에서 빠짐 (P1-4)
            invalidateSummaryCache(statement?.fromUserId || targetId);
            await fetchStatement();
        } catch (e) {
            console.error('정정됨 표시 다시 시도 실패:', e);
            alert(`'정정됨' 표시에 실패했습니다. (오류: ${e?.code || e?.message || '알 수 없음'})\n잠시 후 다시 시도해주세요.`);
        } finally {
            setRetryingCorrected(false);
        }
    };
    const canReview = !readOnly && isReceiver && !isDeletedBySender && statement?.status === 'confirmed' && (!statement?.settlementStatus || statement?.settlementStatus === 'pending' || statement?.settlementStatus === 'disputed');

    useEffect(() => {
        fetchStatement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statementId]);

    // 수신자가 검토 가능한 상태면 자동으로 검토 모드 진입
    useEffect(() => {
        if (canReview) {
            setReviewing(true);

            // 이전 수정요청 내용이 있으면 미리 채워넣기 — 양식 줄 강조와 같은 규칙 (2026-10-23):
            // 수정 요청됨(disputed)일 때만, 줄은 저장된 키로 찾기(구 노트는 index 폴백)
            const prevMap = statement?.settlementStatus === 'disputed'
                ? disputedIndexMapOf(lastDisputeNoteOf(statement), normalizeStatementItems(statement?.items))
                : {};
            const prevIdx = Object.keys(prevMap);
            if (prevIdx.length > 0) {
                const prevChecked = {};
                const prevReasons = {};
                prevIdx.forEach(idx => {
                    prevChecked[idx] = true;
                    prevReasons[idx] = prevMap[idx] || '';
                });
                setCheckedItems(prevChecked);
                setReviewReasons(prevReasons);
            } else {
                setCheckedItems({});
                setReviewReasons({});
            }
            setReviewGeneralNote('');
        }
    }, [canReview]);

    // 외주 명세서 보기 보강 — 의뢰서를 못 읽으면(지워짐·권한) 그 줄은 저장된 그대로 둔다
    const fillMfFromOrders = async (data) => {
        const ids = data.outsourceMfOrderIds.slice(0, 300);
        const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'outsourceOrders', id)).catch((e) => {
            console.warn('[외주 명세서] 의뢰서 읽기 실패 code=%s', e?.code || e?.message);
            return null;
        })));
        const orders = snaps.filter((x) => x && x.exists()).map((x) => ({ id: x.id, ...x.data() }));
        if (!orders.length) return;
        setEditedItems((cur) => (cur.length === (data.items || []).length ? normalizeStatementItems(fillMfItemsFromOrders(cur, ids, orders)) : cur));
    };

    const fetchStatement = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const docRef = doc(db, 'transactionStatements', statementId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = { id: docSnap.id, ...docSnap.data() };
                setStatement(data);

                // 개수(저장값 우선)·개당가·합산가 정규화 — 공용 util (미리보기와 동일 계산)
                setEditedItems(normalizeStatementItems(data.items));
                // 외주 명세서(11-02): 그 전에 만든 명세서는 줄에 작성일·납기일·임플란트 종류가 없다 → 의뢰서에서 비어 있는 것만 채워 보여 준다
                if (data.kind === 'outsource_mf' && Array.isArray(data.outsourceMfOrderIds) && data.outsourceMfOrderIds.length) {
                    fillMfFromOrders(data);
                }
                setEditedDiscount(data.totalDiscount || data.discount || 0);
                setEditedDiscountType(data.discountType || 'amount');
                setEditedNotes(data.notes || '');
            } else {
                alert('거래명세서를 찾을 수 없습니다.');
                onBack();
            }
        } catch (e) {
            console.error('거래명세서 조회 실패:', e);
            setLoadError(true);
        }
        setLoading(false);
    };

    // parseToothCount("#" 뒤의 숫자만 치아로 인식)는 utils/statementView로 이동 (작성 화면과 동일 규칙)

    // 단가표 매칭: 공용 findPriceMatch(별칭 사전·표준키 접기 포함, utils/statementItems.js) 사용 (2026-08-26)
    // — 자체 부분포함 매칭은 CAD 영문 표기("AnatomicCrown")와 수기 단가표("크라운")를 못 잇던 원인이라 제거

    // 스프레드시트식 키보드 이동: Enter로 다음 칸, 마지막 칸에서 Enter면 새 행 (작성 화면과 동일)
    const handleGridKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        const target = e.target;
        if (target.tagName !== 'INPUT' || !target.dataset.nav) return;
        e.preventDefault();
        const inputs = Array.from(document.querySelectorAll('input[data-nav]'));
        const idx = inputs.indexOf(target);
        if (idx >= 0 && idx < inputs.length - 1) {
            inputs[idx + 1].focus();
            inputs[idx + 1].select?.();
        } else {
            handleAddItem();
            setTimeout(() => {
                const patients = document.querySelectorAll('input[data-nav="patient"]');
                patients[patients.length - 1]?.focus();
            }, 50);
        }
    };

    const getPartnerId = () => {
        if (!statement) return null;
        return isCreator ? statement.toUserId : statement.fromUserId;
    };

    const loadPriceList = async (partnerId) => {
        try {
            const uid = user?.companyId || user?.uid;
            const docRef = doc(db, 'priceLists', `${uid}_${partnerId}`);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                // 앱(name/price)과 웹(serviceName/unitPrice) 필드 호환 + material 포함 — 공용 정규화 (2026-08-26)
                setPriceList(normalizePriceItems(docSnap.data().items));
            } else {
                setPriceList([]);
            }
        } catch (e) {
            console.error('단가표 로드 실패:', e);
            setPriceList([]);
        }
    };

    useEffect(() => {
        if (statement) {
            const partnerId = getPartnerId();
            if (partnerId) loadPriceList(partnerId);
            loadPricePresets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statement?.id]);

    const loadPricePresets = async () => {
        try {
            const tid = user?.companyId || user?.uid;
            if (!tid) return;
            const userDoc = await getDoc(doc(db, 'users', tid));
            if (!userDoc.exists()) return;
            const data = userDoc.data();

            const prosSet = new Set(DEFAULT_PROSTHESIS);
            (data.savedProsthesisOptions || []).forEach(opt => {
                const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                if (label) prosSet.add(label);
            });
            (data.customProsthesisOptions || []).forEach(opt => {
                const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                if (label) prosSet.add(label);
            });
            setProsthesisPresets(Array.from(prosSet));

            const impSet = new Set(DEFAULT_IMPLANTS);
            (data.savedImplantOptions || []).forEach(opt => {
                const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                if (label) impSet.add(label);
            });
            (data.customImplantOptions || []).forEach(opt => {
                const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                if (label) impSet.add(label);
            });
            setImplantPresets(Array.from(impSet));
        } catch (e) {
            console.log('프리셋 로드 실패:', e);
        }
    };

    // 🔗 비회원 치과용 공유 링크 (2026-10-24 2단계 ⑥) — 스냅샷·토큰·이름 가림·만료(60일)는 서버 createStatementShare 가 만든다.
    // 화면은 링크 상태(만료일·치과 확인·수정 요청)를 statementShares/{shareToken} 에서 읽어 보여 주기만 한다.
    const [sharing, setSharing] = useState(false);
    // shareInfo: null=링크 없음/아직 모름, { token, readable:false, code } = 읽기 실패(만료·옛 링크), { token, readable:true, ... }
    const [shareInfo, setShareInfo] = useState(null);
    const loadShareInfo = async (token) => {
        if (!token) { setShareInfo(null); return; }
        try {
            const snap = await getDoc(doc(db, 'statementShares', token));
            if (!snap.exists()) { setShareInfo({ token, readable: false, code: 'not-found' }); return; }
            const d = snap.data();
            setShareInfo({
                token, readable: true,
                expiresAt: d.expiresAt || null, createdAt: d.createdAt || null,
                confirmedAt: d.confirmedAt || null, confirmedName: d.confirmedName || '',
                revisions: shareRevisionsOf(d), revisionCount: Number(d.revisionCount) || 0,
            });
        } catch (e) {
            // rules: 만료됐거나 expiresAt 없는 옛 링크는 get 자체가 거부된다 → '다시 발급' 안내
            console.warn('공유 링크 상태 조회 실패:', statementId, e?.code || e?.message);   // 토큰은 남기지 않는다
            setShareInfo({ token, readable: false, code: e?.code || 'error' });
        }
    };
    useEffect(() => {
        if (!statement?.id || !isCreator || readOnly || statement?.status === 'draft') { setShareInfo(null); return; }
        loadShareInfo(statement.shareToken || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statement?.id, statement?.shareToken, isCreator, readOnly, statement?.status]);

    const copyShareUrl = async (url, expiresAt, renewed) => {
        try { await navigator.clipboard.writeText(url); }
        catch (e) {
            console.warn('클립보드 복사 실패 — 직접 복사 안내:', e?.name || e?.message);
            window.prompt('아래 링크를 복사하세요:', url);
        }
        const until = mdOf(expiresAt);
        // 3줄 안내 (2026-10-24 재점검 UX) — URL은 이미 클립보드에 있으니 창에는 넣지 않는다
        alert(`공유 링크를 복사했습니다${until ? ` (~${until}까지)` : ''}.${renewed ? '\n새 링크를 발급했습니다. 이전 링크는 더 이상 열리지 않습니다.' : ''}\n카톡·문자에 붙여 치과에 보내세요. 치과는 가입 없이 보고 [확인]·[수정 요청]을 보낼 수 있습니다.\n환자 이름은 가운데가 가려져 보입니다.`);
    };

    // reissue=false: 살아 있는 링크가 있고 그 뒤로 명세서가 바뀌지 않았으면 그대로 복사, 아니면 서버에서 새로 발급
    const handleShareLink = async (reissue = false) => {
        if (!statement || sharing) return;   // 이중 클릭 방지
        const alive = shareInfo?.readable && msOf(shareInfo.expiresAt) > Date.now();
        // 링크 만든 뒤 명세서가 수정됐으면 스냅샷이 옛 내용 — 새로 발급
        const stale = alive && msOf(statement.updatedAt) > msOf(shareInfo.createdAt) && msOf(shareInfo.createdAt) > 0;
        if (!reissue && alive && !stale) {
            await copyShareUrl(`${window.location.origin}/s/${shareInfo.token}`, shareInfo.expiresAt, false);
            return;
        }
        if (reissue && shareInfo?.token && !window.confirm('새 링크를 발급할까요?\n\n이전 링크는 더 이상 열리지 않습니다. 치과가 이전 링크에서 누른 확인·수정 요청 기록은 명세서 이력에 남아 있습니다.')) return;
        setSharing(true);
        try {
            const res = await callFn('createStatementShare')({ statementId });
            const { token, url, expiresAt } = res?.data || {};
            if (!token) throw new Error('서버가 링크를 돌려주지 않았습니다');
            setStatement(prev => ({ ...prev, shareToken: token }));   // 서버가 명세서 shareToken 을 갱신함 — 화면만 맞춘다
            await copyShareUrl(url || `${window.location.origin}/s/${token}`, expiresAt, !!(statement.shareToken && statement.shareToken !== token));
            await loadShareInfo(token);
        } catch (e) {
            console.error('공유 링크 생성 실패:', statementId, e?.code || e?.message);
            const code = String(e?.code || '').replace(/^functions\//, '');
            const msg = code === 'permission-denied' ? '이 명세서의 링크를 만들 권한이 없습니다.'
                : code === 'failed-precondition' ? '발행된 명세서만 링크를 만들 수 있습니다.'
                : code === 'not-found' ? '명세서를 찾을 수 없습니다.'
                : `잠시 후 다시 시도해 주세요. (오류: ${code || e?.message || '알 수 없음'})`;
            alert(`공유 링크 생성에 실패했습니다.\n${msg}`);
        } finally {
            setSharing(false);
        }
    };

    // ── [계산서 바로 발행] (2026-10-24 2단계 ⑤ · 계약 §1·§5) — 금액·당사자·작성일은 서버가 명세서에서 다시 읽는다 ──
    const [oneClicking, setOneClicking] = useState(false);
    const oneClickRef = React.useRef(false);   // 상태 반영 전 연타 차단
    const [popbillLive, setPopbillLive] = useState(null);   // null=조회 중/실패(잠금으로 취급)
    useEffect(() => {
        if (!isCreator || readOnly) return;
        let alive = true;
        getFeatures()
            .then(f => { if (alive) setPopbillLive(f?.popbillLive === true); })
            .catch(e => { console.warn('기능 플래그 조회 실패(전자발행 잠금으로 표시):', e?.code || e?.message); if (alive) setPopbillLive(false); });
        return () => { alive = false; };
    }, [isCreator, readOnly]);
    // 연결된 계산서 상태 (발행 후 칩에 '국세청 전송됨 / 덴클 발행 기록' 구분)
    const [linkedInvoice, setLinkedInvoice] = useState(null);
    useEffect(() => {
        setLinkedInvoice(null);
        // 잠금 중(pending:{id})이면 그 계산서를 읽어 멈춤 여부(10분)를 판단한다
        const tid = String(statement?.taxInvoiceId || '').replace(/^pending:/, '');
        if (!isCreator || !tid) return;
        let alive = true;
        getDoc(doc(db, 'taxInvoices', tid))
            .then(snap => { if (alive && snap.exists()) setLinkedInvoice({ id: snap.id, ...snap.data() }); })
            .catch(e => console.warn('연결 계산서 조회 실패:', statementId, e?.code || e?.message));
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statement?.taxInvoiceId, isCreator]);

    // retry=true: 멈춘 발행(pending 10분 초과) 이어서 처리 — 같은 callable 재호출(서버가 같은 문서번호로 재개, 이중 발행 없음)
    const handleOneClickInvoice = async (retry = false) => {
        if (oneClickRef.current || oneClicking || !statement) return;
        if (retry === true) {
            if (!window.confirm('멈춘 계산서 발행을 다시 시도할까요?\n같은 계산서로 이어서 처리하므로 두 번 발행되지 않습니다.')) return;
            return runOneClick();
        }
        const nonMember = isNonMemberStatement(statement);
        const total = Math.round(Number(statement.totalAmount) || 0);
        // 작성일 = 기간 종료일, 없으면 오늘 (명세 §3-4)
        const writeDate = toDateAny(statement.endDate) || new Date();
        const wd = `${writeDate.getMonth() + 1}/${writeDate.getDate()}`;
        // 발급 기한(다음 달 10일) 경과 경고 — 막지는 않는다
        const deadline = new Date(writeDate.getFullYear(), writeDate.getMonth() + 1, 10, 23, 59, 59);
        const lateLine = Date.now() > deadline.getTime() ? '\n\n⚠️ 발급 기한(다음 달 10일)이 지났을 수 있습니다. 세무사와 확인하세요.' : '';
        const lockLine = popbillLive === true ? '\n\n발행하면 국세청 전자발행까지 진행됩니다.' : `\n\n※ ${ONECLICK_LOCKED_NOTE}. 국세청에는 전송되지 않습니다.`;
        const nonMemberLine = nonMember ? `\n\n${ONECLICK_NONMEMBER_NOTE}` : '';
        const msg = `${statement.toUserName || '치과'}에 면세 계산서 ${total.toLocaleString()}원, 작성일 ${wd}.\n품목: 치과기공료 요약 1줄(환자 이름 없음)\n\n발행할까요?`
            + lockLine + nonMemberLine + lateLine;
        if (!window.confirm(msg)) return;
        return runOneClick();
    };
    const runOneClick = async () => {
        oneClickRef.current = true;
        setOneClicking(true);
        try {
            const res = await callFn('issueInvoiceFromStatement')({ statementId });
            const { mode } = res?.data || {};
            const labId = statement.fromUserId || targetId;
            invalidateReceivables(labId);
            invalidateSummaryCache(labId);
            // 영문 상태값(electronicStatus)은 화면에 내보내지 않는다 (2026-10-24 재점검 UX)
            alert(mode === 'live' ? '계산서를 발행했습니다. 국세청 전자발행을 진행했습니다.'
                : mode === 'test' ? '계산서를 발행했습니다. (팝빌 테스트 발행 — 국세청 미전송)'
                : '계산서를 덴클 안에만 기록했습니다. 국세청 전송은 아직 열리지 않았습니다.');
            await fetchStatement();
        } catch (e) {
            console.error('원클릭 계산서 발행 실패:', statementId, e?.code || e?.message);   // 품목·환자 정보 없음
            const code = String(e?.code || '').replace(/^functions\//, '');
            const reason = e?.message && code !== 'internal' ? e.message : '';
            alert(`계산서 발행에 실패했습니다.\n${code === 'permission-denied' ? '이 명세서로 계산서를 발행할 권한이 없습니다.'
                : code === 'failed-precondition' ? (reason || '정산 확인(비회원은 링크 확인) 전이거나 이미 계산서가 있습니다.')
                : `잠시 후 다시 시도해 주세요. (오류: ${reason || code || '알 수 없음'})`}`);
            if (code === 'failed-precondition') await fetchStatement();
        } finally {
            oneClickRef.current = false;
            setOneClicking(false);
        }
    };

    const handleAddPricePreset = (presetName) => {
        if (editingPriceList.some(item => item.serviceName === presetName)) {
            alert(`"${presetName}"은(는) 이미 추가되어 있습니다.`);
            return;
        }
        setEditingPriceList([...editingPriceList, { serviceName: presetName, unitPrice: 0 }]);
    };

    const savePriceList = async () => {
        const partnerId = getPartnerId();
        if (!partnerId) return;
        try {
            const partnerName = isCreator ? statement.toUserName : statement.fromUserName;
            const validItems = editingPriceList
                .filter(item => (item.serviceName || '').trim())
                .map(item => ({
                    serviceName: item.serviceName,
                    unitPrice: Number(item.unitPrice) || 0,
                    name: item.serviceName,
                    price: Number(item.unitPrice) || 0,
                }));
            const uid = user?.companyId || user?.uid;
            const docRef = doc(db, 'priceLists', `${uid}_${partnerId}`);
            await setDoc(docRef, {
                userId: uid,
                partnerId,
                partnerName: partnerName || '',
                items: validItems,
                updatedAt: Timestamp.now(),
            });
            setPriceList(validItems);
            setShowPriceListModal(false);
            alert('단가표가 저장되었습니다.');
        } catch (e) {
            console.error('단가표 저장 실패:', e);
            alert('저장에 실패했습니다.');
        }
    };

    const openPriceListModal = () => {
        setEditingPriceList(priceList.length > 0 ? priceList.map(p => ({ ...p })) : [{ serviceName: '', unitPrice: 0 }]);
        setShowPriceListModal(true);
    };

    const applyPriceList = () => {
        if (priceList.length === 0) {
            alert('저장된 단가표가 없습니다. 단가표를 먼저 등록해주세요.');
            return;
        }
        let matchCount = 0;
        const updated = editedItems.map(item => {
            if (item.itemType === 'gold') return item;   // 금값 파생 행은 중량×시세 고정 — 단가표 일괄 적용에서 제외
            const match = findPriceMatch(priceList, item.toothInfo, item.serviceName, item.material);
            if (match) {
                matchCount++;
                const pricePerTooth = match.unitPrice;
                const totalPrice = pricePerTooth * (item.toothCount || 0);
                return { ...item, pricePerTooth, unitPrice: pricePerTooth, price: totalPrice, totalPrice };
            }
            return item;
        });
        setEditedItems(updated);
        alert(`${editedItems.length}건 중 ${matchCount}건에 단가가 적용되었습니다.`);
    };

    const handleAddItem = () => {
        setEditedItems([...editedItems, {
            patientName: '',
            toothInfo: '',
            toothCount: 0,
            pricePerTooth: 0,
            unitPrice: 0,
            price: 0,
            totalPrice: 0,
            createdAt: null,
            dueDate: null,
            isRemake: false,
            remakeReason: '',
        }]);
    };

    const handlePricePerToothChange = (index, value) => {
        const newItems = [...editedItems];
        const pricePerTooth = Number(value) || 0;
        if (pricePerTooth < 0) return;
        newItems[index].pricePerTooth = pricePerTooth;
        newItems[index].unitPrice = pricePerTooth;
        newItems[index].price = pricePerTooth * (newItems[index].toothCount || 0);
        newItems[index].totalPrice = newItems[index].price;
        setEditedItems(newItems);
    };

    const handleToothInfoChange = (index, value) => {
        const newItems = [...editedItems];
        newItems[index].toothInfo = value;
        // 사용자가 개수를 직접 입력한 항목은 자동 계산으로 덮어쓰지 않음
        if (!newItems[index].countManual) {
            newItems[index].toothCount = parseToothCount(value);
        }
        // 단가표 항목이 포함되면 단가 자동 입력 (직접 입력한 단가는 유지)
        if (!(newItems[index].pricePerTooth || newItems[index].unitPrice)) {
            const match = findPriceMatch(priceList, value, newItems[index].serviceName, newItems[index].material);
            if (match) {
                newItems[index].pricePerTooth = match.unitPrice;
                newItems[index].unitPrice = match.unitPrice;
            }
        }
        const count = newItems[index].toothCount || 0;
        newItems[index].price = (newItems[index].pricePerTooth || newItems[index].unitPrice || 0) * count;
        newItems[index].totalPrice = newItems[index].price;
        setEditedItems(newItems);
    };

    const handleToothCountChange = (index, value) => {
        const newItems = [...editedItems];
        const count = Number(value) || 0;
        if (count < 0) return;
        newItems[index].toothCount = count;
        newItems[index].countManual = true; // 직접 입력 표시 → 이후 치아정보 수정 시 덮어쓰기 방지
        newItems[index].price = (newItems[index].pricePerTooth || newItems[index].unitPrice || 0) * count;
        newItems[index].totalPrice = newItems[index].price;
        setEditedItems(newItems);
    };

    const removeItem = (index) => {
        // 부모 품목 행을 지우면 연결된 금값 파생 행(itemType 'gold')도 동반 삭제 — 작성 화면과 동일 규칙 (2026-08-20)
        const target = editedItems[index];
        setEditedItems(editedItems.filter((it, i) =>
            i !== index &&
            !(target?.goldLineId && it.itemType === 'gold' && it.goldForItemId === target.goldLineId)
        ));
    };

    // 소계·할인·합계·치아수 — 공용 util (금값 행 개수 제외 규칙 포함, 미리보기와 동일 계산)
    const totals = useMemo(
        () => computeStatementTotals(editedItems, editedDiscount, editedDiscountType),
        [editedItems, editedDiscount, editedDiscountType],
    );

    const handleSave = async () => {
        if (savingEdit) return;
        // 정산 완료본은 수정 불가 (2026-10-23) — 버튼이 숨겨져 있어도 경로 차단
        if (statement?.settlementStatus === 'settled') {
            alert('정산 완료된 명세서는 수정할 수 없습니다.\n틀린 곳이 있으면 [고쳐서 다시 발행]을 이용해주세요.');
            return;
        }
        setSavingEdit(true);
        try {
            if (totals.total < 0) {
                alert('할인 금액이 총액보다 클 수 없습니다.');
                return;
            }
            const itemsToSave = editedItems.map(item => ({
                ...item,
                unitPrice: item.pricePerTooth || item.unitPrice || 0,
                pricePerTooth: item.pricePerTooth || item.unitPrice || 0,
                totalPrice: item.price || item.totalPrice || 0,
                price: item.price || item.totalPrice || 0,
                toothCount: item.toothCount || 0,
            }));

            const updateData = {
                items: itemsToSave,
                totalDiscount: Number(editedDiscount),
                discountType: editedDiscountType,
                subtotal: totals.subtotal,
                discountAmount: totals.discountAmount,
                totalAmount: totals.total,
                totalTeeth: totals.totalTeeth,
                notes: editedNotes,
                updatedAt: new Date(),
                status: statement.status || 'confirmed',
            };

            if (statement.status === 'confirmed' && statement.settlementStatus) {
                updateData.settlementStatus = 'pending';
                const systemNote = {
                    id: `note_${Date.now()}`,
                    text: '명세서가 수정되었습니다.',
                    authorId: 'system',
                    authorName: '시스템',
                    createdAt: new Date().toISOString(),
                    type: 'system',
                };
                updateData.settlementNotes = [...(statement.settlementNotes || []), systemNote];
                updateData.settledAt = null;
                updateData.settledBy = null;
                updateData.settledByName = null;
            }

            await updateDoc(doc(db, 'transactionStatements', statementId), updateData);
            // 금액·항목이 바뀌었으니 원장·카드 캐시를 버린다 (2026-10-24 재점검 P2-8)
            invalidateReceivables(statement.fromUserId || targetId);
            invalidateSummaryCache(statement.fromUserId || targetId);

            // 세금계산서가 이미 발행된 명세서 — 금액이 달라졌으면 계산서 재발행 안내 (2026-07-25)
            if (statement.taxInvoiceId && statement.taxInvoiceStatus === 'issued' && totals.total !== (statement.totalAmount || 0)) {
                alert(`⚠️ 이 명세서로 발행된 세금계산서가 있습니다.\n\n명세서 금액이 ${(statement.totalAmount || 0).toLocaleString()}원 → ${totals.total.toLocaleString()}원으로 변경되어 세금계산서와 금액이 달라졌습니다.\n\n[세금계산서] 메뉴에서 기존 계산서를 삭제한 뒤, 수정된 금액으로 다시 발행해주세요.`);
            }

            // 발행본 수정 → 공유 링크 스냅샷은 옛 금액 그대로다. 옛 링크 문서를 지워 치과가 옛 내용을 보지 못하게 하고, 다시 보내도록 안내 (2026-10-24 재점검 P1-1)
            let shareLine = '';
            if (statement.status === 'confirmed' && statement.shareToken) {
                try {
                    await deleteDoc(doc(db, 'statementShares', statement.shareToken));
                    setShareInfo(null);
                } catch (shareErr) {
                    console.warn('수정 저장 뒤 옛 공유 링크 삭제 실패:', statementId, shareErr?.code || shareErr?.message);   // 토큰은 남기지 않는다
                    shareLine = '\n\n이전 공유 링크를 지우지 못했습니다. 치과가 옛 금액을 볼 수 있으니 [링크 다시 발급]을 눌러 주세요.';
                }
            }
            if (!shareLine && statement.status === 'confirmed' && isNonMemberStatement(statement)) {
                shareLine = '\n\n비회원 치과에는 공유 링크를 다시 보내 주세요. (이전 링크는 더 이상 열리지 않습니다)';
            }

            // 발행된 명세서를 수정한 경우 상대방에게 알림 (수정요청 → 재수정 흐름 포함)
            if (statement.status === 'confirmed') {
                const partnerId = getPartnerId();
                if (!isGhostRecipientId(partnerId)) {
                    try {
                        await createNotification({
                            recipientId: partnerId,
                            type: 'statement_updated',
                            title: '거래명세서 수정',
                            message: `${statement.fromUserName || '거래처'}에서 거래명세서를 수정했습니다.${statement.statementNumber ? ` (${statement.statementNumber})` : ''} 내용을 다시 확인해주세요.`,
                            link: '/transactions',
                        });
                    } catch (notifError) {
                        console.error('수정 알림 전송 실패:', notifError);
                    }
                }
            }

            alert(`저장되었습니다.${shareLine}`);
            setEditing(false);
            fetchStatement();
        } catch (e) {
            console.error('저장 실패:', e);
            alert('저장에 실패했습니다.');
        } finally {
            setSavingEdit(false);
        }
    };

    // 삭제 정책 (2026-08-26 대표 확정):
    // - draft = 기존대로 하드 삭제
    // - 발행본 = 기간 무관 소프트 삭제 (deletedBySender 표시 + 공유 스냅샷 삭제, 받는 쪽에는 "발행처에서 삭제함" 배지)
    const handleDelete = async () => {
        if (isDraft) {
            if (!window.confirm('작성 중인 거래명세서를 삭제하시겠습니까?')) return;
            setDeleting(true);
            try {
                // (2026-10-23 1단계) 명세서 삭제 + 이 명세서에 묶인 수집 케이스 되돌림(pending)을 writeBatch 하나로 — 케이스가 조용히 사라지지 않게
                await deleteDraftAndRestoreCases(statementId, statement?.fromUserId || targetId);   // 로그(id·건수)는 caseRestore가 남김
                invalidateReceivables(statement?.fromUserId || targetId);   // 원장·카드 캐시 지움 (P1-4)
                invalidateSummaryCache(statement?.fromUserId || targetId);
                onBack();
            } catch (e) {
                // 조용한 실패 금지 — 왜 안 지워졌는지 사유를 화면에 띄운다 (2026-08-08)
                console.error('삭제 실패:', e);
                alert(getStatementDeleteErrorMessage(e, statement, user));
            } finally {
                setDeleting(false);
            }
            return;
        }
        // 세금계산서 연결 가드 유지 — 계산서가 발행된 명세서는 계산서 정리가 먼저
        // (P2-4, 2026-08-26) issued만이 아니라 "연결돼 있고 취소류가 아니면" 전부 차단 (draft 계산서 등 포함)
        if (statement?.taxInvoiceId && !CANCELED_LIKE_TAX.includes(statement?.taxInvoiceStatus)) {
            alert('이 명세서에 연결된 세금계산서가 있습니다.\n[세금계산서] 메뉴에서 계산서를 먼저 삭제(취소)한 뒤 명세서를 삭제해주세요.');
            return;
        }
        if (!window.confirm(`발행된 거래명세서를 삭제하시겠습니까?\n\n받는 곳에는 '발행처에서 삭제함'으로 표시됩니다.\n(공유 링크도 함께 삭제됩니다)`)) return;
        setDeleting(true);
        try {
            // (2026-10-23 1단계) 삭제 표시 + (정정이 아니면) 묶인 수집 케이스 되돌림을 writeBatch 하나로 — 기존 3키 계약 그대로
            await markDeletedAndRestoreCases(statementId, statement?.fromUserId || targetId, {
                deletedBySender: true,
                deletedBySenderAt: serverTimestamp(),
            });   // 로그(id·건수)는 caseRestore가 남김
            invalidateReceivables(statement?.fromUserId || targetId);   // 삭제 표시로 받을 돈이 바뀜 (P1-4)
            invalidateSummaryCache(statement?.fromUserId || targetId);
            // 공유 스냅샷도 함께 삭제 — 링크로 열람 중인 치과에 삭제본이 남지 않게
            if (statement?.shareToken) {
                try { await deleteDoc(doc(db, 'statementShares', statement.shareToken)); }
                catch (e) {
                    console.warn('공유 스냅샷 삭제 실패:', e);
                    // (P2-6, 2026-08-26) 사용자에게도 알림 — 명세서 삭제 자체는 진행
                    alert('공유 링크 삭제에 실패했습니다. 링크가 계속 열릴 수 있으니 다시 시도해 주세요.');
                }
            }
            onBack();
        } catch (e) {
            console.error('삭제 처리 실패:', e);
            alert(`삭제 처리에 실패했습니다. (오류: ${e?.code || e?.message || '알 수 없음'})\n잠시 후 다시 시도해주세요.`);
        } finally {
            setDeleting(false);
        }
    };

    // [고쳐서 다시 발행] (2026-10-23 대표 확정) — 정산 완료본은 고치지 않고, 같은 내용으로 새 명세서를 연다.
    // 새 명세서가 발행되면 작성 화면이 이 명세서에 deletedBySender·correctedTo를 표시한다
    const handleCorrect = () => {
        // 삭제 가드와 같은 기준 — 계산서가 연결돼 있고 취소류가 아니면 차단 (2026-10-23 검토 회의)
        if (statement?.taxInvoiceId && !CANCELED_LIKE_TAX.includes(statement?.taxInvoiceStatus)) {
            alert('이 명세서에 연결된 세금계산서가 있습니다.\n[세금계산서] 메뉴에서 계산서를 먼저 삭제(취소)한 뒤 정정해주세요.');
            return;
        }
        if (!window.confirm("정산 완료된 명세서를 고쳐서 다시 발행합니다.\n같은 내용으로 새 명세서가 열리고, 새 명세서를 발행하면 이 명세서는 '정정됨'으로 바뀝니다.")) return;
        if (onCorrect) onCorrect(statementId);
    };

    // ── [발행하기] 전월 미수 선택 (2026-10-24 R-1) — 작성 화면과 같은 규칙:
    //    대표만(직원 X)·원장 대상 거래처만·기본 꺼짐·0원 이하 숨김·발행 직전 force 재계산·대표 본인 계정만 carryOver 키
    const isLabOwnerUser = ['lab', 'outsource'].includes(user?.businessType || user?.companyBusinessType || '') && !isLabStaff(user);
    const viewPartnerKey = (statement && !isOutsourceStatement) ? (partnerKeyOfStatement(statement) || '') : '';
    const [publishModal, setPublishModal] = useState(null);   // null | { loading, preview: {amount, asOf}|null }
    const [includeCarryOver, setIncludeCarryOver] = useState(false);
    const carryOverExcludeIds = [statementId, statement?.correctsId].filter(Boolean);
    const openPublishModal = async () => {
        if (statement?.settlementStatus === 'settled' || publishing) return;   // 정산 완료본 잠금 (2026-10-23)
        setIncludeCarryOver(false);
        const canCarry = isLabOwnerUser && !!viewPartnerKey;
        setPublishModal({ loading: canCarry, preview: null });
        if (!canCarry) return;
        try {
            // 미리보기는 원장 캐시 사용. 이번 명세서·정정 원본 제외, 귀속일 = 기간 종료일
            const co = await computeCarryOver(statement.fromUserId || targetId, viewPartnerKey, new Date(), carryOverExcludeIds,
                { attributionDate: statement.endDate || null });
            setPublishModal({ loading: false, preview: { amount: Math.round(Number(co?.amount) || 0), asOf: co?.asOf || '' } });
        } catch (e) {
            console.warn('전월 미수 미리 계산 실패:', statementId, e?.code || e?.message);
            setPublishModal({ loading: false, preview: null });
        }
    };
    const carryOverAllowed = !!publishModal?.preview && publishModal.preview.amount > 0;

    // 정정본(correctsId)은 작성 화면이 원본에 '정정됨'을 표시하며 발행한다 — 여기서 발행하면 원본이 그대로 남아 두 장이 청구됨 (2026-10-24 재점검 P2-7)
    const isCorrectionDraft = isDraft && !!statement?.correctsId;
    const handlePublish = async () => {
        if (statement?.settlementStatus === 'settled') return;   // 정산 완료본 잠금 (2026-10-23)
        if (isCorrectionDraft) {
            alert('정정 명세서는 작성 화면에서 발행해 주세요.\n(작성 화면이 원래 명세서에 \'정정됨\' 표시를 함께 합니다)');
            return;
        }
        if (publishing) return;   // 이중 클릭 방지
        // 전월 미수 — [명세서에 적기]를 켠 경우에만 발행 시점에 다시 계산해 고정. 합계(totalAmount)에는 더하지 않는다
        let carryOverField = null;
        const labId = statement.fromUserId || targetId;
        // rules: carryOver 는 대표 본인(uid == fromUserId)만 — 직원 계정이면 키 자체를 넣지 않는다
        const isOwnerWriter = !!auth.currentUser?.uid && auth.currentUser.uid === labId;
        if (carryOverAllowed && includeCarryOver && isOwnerWriter) {
            try {
                const co = await computeCarryOver(labId, viewPartnerKey, new Date(), carryOverExcludeIds,
                    { force: true, attributionDate: statement.endDate || null });
                const amt = Math.round(Number(co?.amount) || 0);
                if (amt > 0) {
                    carryOverField = { amount: amt, asOf: co.asOf, computedAt: new Date(), include: true };
                } else if (!window.confirm('다시 계산해 보니 전월 미수가 없습니다(0원 이하). 전월 미수 없이 발행할까요?')) {
                    return;
                }
            } catch (e) {
                console.error('전월 미수 계산 실패:', statementId, e?.code || e?.message);
                if (!window.confirm('전월 미수를 계산하지 못했습니다. 전월 미수 없이 발행할까요?\n(취소하면 발행하지 않습니다)')) return;
            }
        } else if (carryOverAllowed && includeCarryOver && !isOwnerWriter) {
            if (!window.confirm('전월 미수는 기공소 대표 계정에서만 적을 수 있습니다. 전월 미수 없이 발행할까요?')) return;
        }
        setPublishModal(null);
        setPublishing(true);
        try {
            const itemsToSave = editedItems.map(item => ({
                ...item,
                unitPrice: item.pricePerTooth || item.unitPrice || 0,
                pricePerTooth: item.pricePerTooth || item.unitPrice || 0,
                totalPrice: item.price || item.totalPrice || 0,
                price: item.price || item.totalPrice || 0,
                toothCount: item.toothCount || 0,
            }));

            await updateDoc(doc(db, 'transactionStatements', statementId), {
                items: itemsToSave,
                totalDiscount: Number(editedDiscount),
                discountType: editedDiscountType,
                subtotal: totals.subtotal,
                discountAmount: totals.discountAmount,
                totalAmount: totals.total,
                totalTeeth: totals.totalTeeth,
                notes: editedNotes,
                updatedAt: new Date(),
                publishedAt: new Date(),
                status: 'confirmed',
                settlementStatus: 'pending',
                settlementNotes: [],
                settledAt: null,
                settledBy: null,
                settledByName: null,
                ...(carryOverField ? { carryOver: carryOverField } : {}),   // 전월 미수 고정값 — totalAmount와 별개
            });

            // 비회원 이름 키(email_/agent_/order_/name:)에는 알림을 만들지 않는다 — 작성 화면과 같은 가드 (P2-12)
            if (!isGhostRecipientId(statement.toUserId)) {
                try {
                    await createNotification({
                        recipientId: statement.toUserId,
                        type: 'statement_received',
                        title: '거래명세서 수신',
                        message: `${statement.fromUserName}에서 거래명세서를 발행했습니다.${statement.statementNumber ? ` (${statement.statementNumber})` : ''}`,
                        link: '/transactions',
                    });
                } catch (e) {
                    console.error('발행 알림 전송 실패:', statementId, e?.code || e?.message);
                }
            }

            invalidateReceivables(statement?.fromUserId || targetId);   // 발행으로 받을 돈이 바뀜 (P1-4)
            invalidateSummaryCache(statement?.fromUserId || targetId);
            alert('거래명세서가 발행되었습니다.');
            fetchStatement();
        } catch (e) {
            console.error('발행 실패:', statementId, e?.code || e?.message);
            alert('발행에 실패했습니다: ' + (e?.message || e?.code || '알 수 없는 오류'));   // 조용한 실패 금지 (2026-08-26)
        } finally {
            setPublishing(false);
        }
    };

    // 정산 검토 항목 체크 토글
    const toggleCheckItem = (index) => {
        setCheckedItems(prev => {
            const updated = { ...prev };
            if (updated[index]) {
                delete updated[index];
                const reasons = { ...reviewReasons };
                delete reasons[index];
                setReviewReasons(reasons);
            } else {
                updated[index] = true;
            }
            return updated;
        });
    };

    // 수정 요청 전송
    const submitReview = async () => {
        if (submittingReview) return;   // 이중 클릭 방지 (2026-10-23)
        const flaggedIndexes = Object.keys(checkedItems).map(Number);
        if (flaggedIndexes.length === 0) {
            alert('수정이 필요한 항목을 체크해주세요.');
            return;
        }
        const hasEmptyReason = flaggedIndexes.some(i => !reviewReasons[i]?.trim());
        if (hasEmptyReason) {
            alert('체크한 모든 항목에 수정 사유를 입력해주세요.');
            return;
        }

        setSubmittingReview(true);
        try {
            const userName = user?.businessName || user?.companyName || user?.name || '수신자';
            const reviewItems = flaggedIndexes.map(i => ({
                index: i,
                patientName: editedItems[i]?.patientName || '-',
                toothInfo: editedItems[i]?.toothInfo || '-',
                reason: reviewReasons[i],
                // 줄 식별 키 (2026-10-23) — 줄 순서가 바뀌어도 같은 줄을 강조하도록. index는 구버전 호환용
                itemKey: statementItemKeyOf(editedItems[i]),
            }));

            const lines = reviewItems.map((ci, i) =>
                `${i + 1}. [${ci.patientName}] ${ci.toothInfo}\n   → ${ci.reason}`
            );
            // 전체 메모 포함 (2026-08-26) — 입력칸은 있었지만 전송 payload에서 빠져 있었음
            const generalNote = (reviewGeneralNote || '').trim();
            const noteText = `수정 요청 (${reviewItems.length}건)\n\n${lines.join('\n\n')}`
                + (generalNote ? `\n\n[전체 메모] ${generalNote}` : '');

            await updateDoc(doc(db, 'transactionStatements', statementId), {
                settlementStatus: 'disputed',
                settlementNotes: [
                    ...(statement.settlementNotes || []),
                    {
                        id: `note_${Date.now()}`,
                        text: noteText,
                        authorId: user?.uid,
                        authorName: userName,
                        createdAt: new Date().toISOString(),
                        type: 'dispute',
                        checkedItems: reviewItems,
                        generalNote,
                    }
                ],
            });

            try {
                await createNotification({
                    recipientId: statement.fromUserId,
                    type: 'settlement_disputed',
                    title: '수정 요청',
                    message: `${userName}이(가) 거래명세서 ${reviewItems.length}건 항목에 수정을 요청했습니다.${statement.statementNumber ? ` (${statement.statementNumber})` : ''}`,
                    link: '/transactions',
                });
            } catch (e) {
                console.log('알림 전송 실패:', e);
            }

            setReviewing(false);
            await fetchStatement();
            alert(`${reviewItems.length}건의 수정 요청이 전송되었습니다.`);
        } catch (e) {
            console.error('수정 요청 실패:', e);
            alert('수정 요청에 실패했습니다.');
        }
        setSubmittingReview(false);
    };

    // 정산 확인
    const confirmStatement = async () => {
        if (submittingReview) return;   // 이중 클릭 방지 (2026-10-23)
        // 법무 고정 문구 (계약 §2, 2026-10-23)
        const coLine = `\n\n${SETTLE_CARRY_OVER_NOTE}`;   // 계약 §2 — 항상 표시
        // 용어: 동작 '정산 확인' · 상태 '정산 완료' (2026-10-24 대표 결정)
        if (!window.confirm('정산을 확인할까요?\n확인한 뒤에는 금액을 바꿀 수 없습니다. 틀린 곳이 있으면 확인하지 말고 [수정 요청]을 보내주세요.' + coLine)) return;
        setSubmittingReview(true);
        try {
            const userName = user?.businessName || user?.companyName || user?.name || '수신자';
            await updateDoc(doc(db, 'transactionStatements', statementId), {
                settlementStatus: 'settled',
                settledAt: new Date().toISOString(),
                settledBy: user?.uid,
                settledByName: userName,
                settlementNotes: [
                    ...(statement.settlementNotes || []),
                    {
                        id: `note_${Date.now()}`,
                        // 전체 메모 포함 (2026-08-26) — 확인 시에도 수신자가 남긴 메모를 함께 저장
                        text: '정산 확인이 끝났습니다. (정산 완료)' + ((reviewGeneralNote || '').trim() ? `\n[전체 메모] ${reviewGeneralNote.trim()}` : ''),
                        authorId: 'system',
                        authorName: '시스템',
                        createdAt: new Date().toISOString(),
                        type: 'system',
                        generalNote: (reviewGeneralNote || '').trim(),
                    }
                ],
            });

            try {
                await createNotification({
                    recipientId: statement.fromUserId,
                    type: 'settlement_confirmed',
                    title: '정산 완료',
                    message: `${userName}이(가) 거래명세서 정산을 확인했습니다. (정산 완료)${statement.statementNumber ? ` (${statement.statementNumber})` : ''}`,
                    link: '/transactions',
                });
            } catch (e) {
                console.log('알림 전송 실패:', e);
            }

            setReviewing(false);
            await fetchStatement();
            alert('정산 확인이 끝났습니다. (정산 완료)');
        } catch (e) {
            console.error('정산 확인 실패:', e);
            alert('정산 확인에 실패했습니다.');
        }
        setSubmittingReview(false);
    };

    // 인쇄 — 공용 printStatement(utils/printStatement.js). 제목(=PDF 제안 파일명)은 명세서번호_거래처, 환자명 없음
    const handlePrint = () => {
        printStatement({ title: statementPrintTitle(statement, isCreator ? statement?.toUserName : statement?.fromUserName) });
    };

    // 공유 링크로 들어온 수정 요청이 있고 치과가 아직 [확인]을 안 누름 — 목록·스테퍼와 같은 기준 (2026-10-24 재점검 P2-6)
    const hasLinkRevision = Number(statement?.shareRevisionCount) > 0 && !statement?.shareConfirmedAt;
    // 비회원 치과(이름 키·직접 입력) — 받는 쪽 정산 응답은 rules상 실제 계정만 가능하므로 정산 단계가 없다 (안내문·스테퍼 공용 조건)
    const isNonMemberClinic = !statement?.toUserId || /^(email_|agent_)/.test(String(statement?.toUserId));

    // 용어 통일 (2026-10-24 대표 결정): 작성 중 / 발행됨 / 수정 요청 중 / 정산 완료
    const getStatusBadge = () => {
        let label, bg, color;
        if (isDraft) {
            label = '작성 중'; bg = '#EEF2FF'; color = '#4338CA';
        } else if (statement.settlementStatus === 'settled') {
            label = '정산 완료'; bg = '#F0FDF4'; color = '#15803D';
        } else if (statement.settlementStatus === 'disputed') {
            label = '수정 요청 중'; bg = '#FEF2F2'; color = '#DC2626';
        } else if (hasLinkRevision) {
            label = '수정 요청 중(링크)'; bg = '#FEF2F2'; color = '#DC2626';
        } else {
            label = '발행됨'; bg = '#FFF7E8'; color = '#B45309';
        }
        return (
            <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: bg, color }}>
                {label}
            </span>
        );
    };

    // 진행 스테퍼 (작성 중 → 발행됨 → 정산 완료) — 10-23 대표 결정 3단계. '치과 확인' 칸 제거, 발행 후엔 '정산 완료'가 현재 칸
    const flowInfo = (() => {
        if (isDraft) {
            return isCreator
                ? { current: 0, tone: 'pri', message: '내역을 확인하고 발행하세요.', small: '발행 전에는 거래처에 보이지 않습니다' }
                : { current: 0, tone: 'wait', message: '기공소가 명세서를 작성 중입니다.', small: '발행되면 알림으로 알려드립니다' };
        }
        // 주의: 이 블록은 로딩 가드보다 먼저 실행됨 — statement가 아직 null일 수 있어 ?. 필수 (2026-07-25 흰화면 수정)
        if (statement?.settlementStatus === 'settled') {
            return isCreator
                ? { current: 3, tone: 'done', message: '정산 확인이 끝났습니다. (정산 완료)', small: statement.taxInvoiceId ? '' : '이어서 [세금계산서 발행]을 진행할 수 있습니다' }
                : { current: 3, tone: 'done', message: '정산 완료된 명세서입니다.', small: '세금계산서가 발행되면 [세금계산서] 메뉴에서 확인할 수 있습니다' };
        }
        if (statement?.settlementStatus === 'disputed') {
            return isCreator
                ? { current: 1, tone: 'warn', message: '거래처에서 수정을 요청했습니다.', small: '아래 요청 항목(주황색 표시)을 확인하고 [수정]으로 고치면 자동으로 재확인 요청이 갑니다' }
                : { current: 1, tone: 'wait', message: '수정 요청을 보냈습니다. 기공소가 수정하는 중입니다.', small: '수정되면 알림으로 알려드립니다' };
        }
        // 발행됨 — 수신처 확인 대기
        return isReceiver
            ? { current: 2, tone: 'pri', message: '내역을 확인해주세요.', small: '문제가 없으면 [정산 확인], 다른 부분이 있으면 항목을 체크해 [수정 요청]을 보내주세요' }
            : (!statement?.toUserId || /^(email_|agent_)/.test(String(statement?.toUserId)))
                // 비회원 치과 — 받는 쪽 확인 단계가 없으므로 '확인 중' 문구 대신 발행 완료로 안내
                ? { current: 1, tone: 'done', message: '발행되었습니다.', small: '치과에 명세서를 전달해 주세요' }
                : { current: 2, tone: 'wait', message: '거래처(치과)가 내용을 확인하는 중입니다.', small: '확인 또는 수정 요청이 오면 알림으로 알려드립니다' };
    })();

    // 최근 dispute 노트에서 flaggedItems 추출 (리뷰 이력 표시용)
    const lastDisputeNote = useMemo(() => lastDisputeNoteOf(statement), [statement]);
    // (행 하이라이트용 disputedIndexMap은 StatementSheet가 lastDisputeNote로부터 직접 계산)

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '60px 0', color: '#94A3B8' }}>로딩 중...</div>;
    }

    // 🔒 당사자 검증 — 발신/수신 업체(회사 단위)와 관리자만 열람 가능
    if (statement && !isCreator && !isReceiver && !isAdminUser(user)) {
        return (
            <div style={{ padding: 60, textAlign: 'center' }}>
                <h3 style={{ color: '#0f172a', marginBottom: 8 }}>접근 권한이 없습니다</h3>
                <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>이 거래명세서의 발신 또는 수신 업체만 열람할 수 있습니다.</p>
                <button onClick={onBack} style={{ padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>목록으로</button>
            </div>
        );
    }

    if (!statement) {
        // 조회 오류와 문서 없음 구분 (2026-10-23)
        if (loadError) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#B91C1C' }}>
                    거래명세서를 불러오지 못했습니다.{' '}
                    <button onClick={fetchStatement}
                        style={{ marginLeft: 6, padding: '6px 14px', background: '#fff', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        다시 시도
                    </button>
                </div>
            );
        }
        return <div style={{ textAlign: 'center', padding: '60px 0', color: '#94A3B8' }}>거래명세서를 찾을 수 없습니다.</div>;
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <style>{STATEMENT_LEDGER_CSS}</style>
            {/* 발행처 삭제 안내 (2026-08-26) — 수신자 화면 */}
            {isDeletedBySender && (
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, marginBottom: 16, fontSize: 13.5, color: '#B91C1C', fontWeight: 600 }}>
                    <AlertTriangle size={18} />
                    {isCorrected ? (
                        <span>정정됨 — 정정 명세서가 새로 발행되었습니다. 이 명세서는 합계에서 빠집니다.{statement?.settlementStatus === 'settled' ? '' : ' 받은 명세서 목록의 [목록에서 제거]로 정리할 수 있습니다.'}</span>
                    ) : (
                        <span>발행처에서 삭제한 명세서입니다. 정산 등 처리를 진행할 수 없습니다.{statement?.settlementStatus === 'settled' ? '' : ' 받은 명세서 목록의 [목록에서 제거]로 정리할 수 있습니다.'}</span>
                    )}
                </div>
            )}
            {/* 정정본 발행 후 원본 '정정됨' 표시가 빠진 경우 — 발행자 화면 (2026-10-23) */}
            {pendingCorrection && !readOnly && (
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, marginBottom: 16, fontSize: 13.5, color: '#B91C1C', fontWeight: 600 }}>
                    <AlertTriangle size={18} />
                    <span style={{ flex: 1, minWidth: 220 }}>
                        이 명세서의 정정본({pendingCorrection.statementNumber || pendingCorrection.id})이 이미 발행됐습니다. 이 명세서를 '정정됨'으로 표시해야 두 장이 모두 청구되지 않습니다.
                    </span>
                    <button onClick={handleRetryMarkCorrected} disabled={retryingCorrected}
                        style={{ padding: '6px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 8, cursor: retryingCorrected ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                        {retryingCorrected ? '표시 중...' : '정정됨 표시 다시 시도'}
                    </button>
                </div>
            )}
            <FlowBar
                steps={['작성 중', '발행됨', '정산 완료']}
                current={flowInfo.current}
                tone={flowInfo.tone}
                message={flowInfo.message}
                small={flowInfo.small}
            />
            {/* 비회원 치과 안내 (2026-10-23 §5-3) — 받는 쪽 정산 응답은 rules상 실제 toUserId 계정만 가능
                (toUserId 비어 있음 = 직접 입력, email_/agent_ = 이름 키) → 단계가 '발행됨'에서 더 진행되지 않음 */}
            {!isDraft && isNonMemberClinic && (
                <div className="no-print" style={{ marginTop: -6, marginBottom: 14, padding: '0 4px', fontSize: 12.5, color: '#64748B' }}>
                    {'이 치과는 덴클 회원이 아니어서 정산 확인 단계가 없습니다'}
                </div>
            )}
            {/* 입금 표시 (2026-10-23 1단계) — 대표 화면 전용, 인쇄 제외. 기공소가 직접 기록한 입금 기준 */}
            {paymentInfo && (
                <div className="no-print" style={{ marginTop: -6, marginBottom: 14, padding: '0 4px', fontSize: 12.5, color: '#475569', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700 }}>{'입금:'}</span>
                    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11.5, fontWeight: 700, ...PAYMENT_STATE_STYLE[paymentInfo.state] }}>
                        {paymentInfo.label}
                    </span>
                    {paymentInfo.state === 'partial' && paymentInfo.remaining != null && (
                        <span>{`· ${paymentInfo.remaining.toLocaleString()}원 남음`}</span>
                    )}
                    <span style={{ color: '#94A3B8', fontSize: 11.5 }}>{'입금은 직접 기록한 것만 반영됩니다'}</span>
                </div>
            )}
            {/* 공유 링크 상태 (2026-10-24 2단계 ⑥) — 발행자 화면. 만료일 · 치과 확인 · 수정 요청(rev1~3) */}
            {isCreator && !readOnly && !isDraft && (statement.shareToken || statement.shareConfirmedAt || Number(statement.shareRevisionCount) > 0) && (() => {
                const info = shareInfo && shareInfo.token === statement.shareToken ? shareInfo : null;
                const alive = info?.readable && msOf(info.expiresAt) > Date.now();
                const confirmedAt = info?.confirmedAt || statement.shareConfirmedAt || null;
                const confirmedName = info?.confirmedName || '';
                // 수정 요청 내용: 서버가 명세서에 남긴 shareRevisionNotes(글·시각, 링크 만료 뒤에도 남음)를 우선, 없으면 살아 있는 링크의 rev1~3
                const serverNotes = Array.isArray(statement.shareRevisionNotes)
                    ? statement.shareRevisionNotes.filter(r => r && typeof r.text === 'string') : [];
                const revs = serverNotes.length > 0
                    ? [...serverNotes].sort((a, b) => msOf(a.at) - msOf(b.at))
                    : (info?.revisions || []);
                const revCount = Math.max(revs.length, Number(info?.revisionCount) || 0, Number(statement.shareRevisionCount) || 0);
                const linkBtn = { padding: '2px 8px', fontSize: 11.5, fontWeight: 700, border: '1px solid #C7D2FE', borderRadius: 6, background: '#fff', color: '#4338CA', cursor: sharing ? 'default' : 'pointer' };
                return (
                    <div className="no-print" style={{ marginTop: -6, marginBottom: 14, padding: '0 4px', fontSize: 12.5, color: '#475569' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700 }}>{'공유 링크'}</span>
                            {statement.shareToken && (info == null ? <span style={{ color: '#94A3B8' }}>{'확인 중...'}</span>
                                : alive ? <span>{`~${mdOf(info.expiresAt)}까지`}</span>
                                : <span style={{ color: '#B45309', fontWeight: 600 }}>{'기간이 지났거나 예전 방식 링크입니다 — 새로 발급해 보내 주세요'}</span>)}
                            {confirmedAt && (
                                <span style={{ color: '#15803D', fontWeight: 600 }}>
                                    {`· 치과 확인 ${mdOf(confirmedAt)}${confirmedName ? `(${confirmedName})` : ''}`}
                                </span>
                            )}
                            {revCount > 0 && (
                                <span style={{ color: '#B91C1C', fontWeight: 700 }}>{`· 수정 요청 ${revCount}건`}</span>
                            )}
                            {statement.shareToken && alive && (
                                <button onClick={() => handleShareLink(false)} disabled={sharing} style={linkBtn}>{sharing ? '처리 중...' : '링크 복사'}</button>
                            )}
                            <button onClick={() => handleShareLink(true)} disabled={sharing} style={linkBtn}>{sharing ? '처리 중...' : '다시 발급'}</button>
                        </div>
                        {/* 수정 요청 내용 — 기공소 화면에만. shareRevisionNotes(명세서) 또는 링크 문서 rev1~rev3 */}
                        {revs.length > 0 && (
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {revs.map((r, i) => (
                                    <div key={i} style={{ padding: '6px 10px', borderLeft: '3px solid #F87171', background: '#FEF2F2', borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#7F1D1D' }}>
                                        {r.text}
                                        <span style={{ marginLeft: 6, fontSize: 11, color: '#94A3B8' }}>{[r.name || '', mdOf(r.at)].filter(Boolean).join(' · ')}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {revCount > revs.length && (
                            <div style={{ marginTop: 4, fontSize: 11.5, color: '#94A3B8' }}>{'수정 요청 내용이 아직 반영되지 않았습니다. 잠시 뒤 새로고침해 주세요.'}</div>
                        )}
                    </div>
                );
            })()}

            {/* 헤더 */}
            {/* 폰: 버튼이 많으면 줄바꿈 (2026-10-24 재점검 P2-16) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                        <ArrowLeft size={24} />
                    </button>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>거래명세서</h2>
                    {getStatusBadge()}
                </div>

                <div style={{ display: 'flex', gap: '8px', position: 'relative', flexWrap: 'wrap' }} className="no-print">
                    {/* 주 버튼 1개 원칙 — 수정은 작성 화면(동일 에디터)으로 이동, 보조는 ⋯ 메뉴 */}
                    {/* 비회원 치과 명세서는 링크가 전달 수단이므로 [공유 링크 복사]를 주 버튼으로 (2026-10-24 재점검 UX) */}
                    {isCreator && !isDraft && !readOnly && !editing && isNonMemberStatement(statement) && (
                        <button onClick={() => handleShareLink(false)} disabled={sharing}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#6366F1', color: '#fff', border: 'none', borderRadius: '8px', cursor: sharing ? 'default' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: sharing ? 0.6 : 1 }}>
                            🔗 {sharing ? '링크 생성 중...' : '공유 링크 복사'}
                        </button>
                    )}
                    {canEdit && !editing && (
                        <button onClick={() => {
                                if (onEdit) { onEdit(statementId); return; }
                                setEditing(true); const pid = getPartnerId(); if (pid) loadPriceList(pid);
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#EEF2FF', color: '#6366F1', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            <Edit2 size={16} /> 수정
                        </button>
                    )}
                    {canCorrect && !editing && (
                        <button onClick={handleCorrect} disabled={publishing || sharing}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#EEF2FF', color: '#6366F1', border: 'none', borderRadius: '8px', cursor: (publishing || sharing) ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: (publishing || sharing) ? 0.6 : 1 }}>
                            <Edit2 size={16} /> 고쳐서 다시 발행
                        </button>
                    )}
                    {/* 정정본(correctsId)은 작성 화면에서만 발행 — 원본 '정정됨' 표시가 함께 가야 함 (P2-7) */}
                    {isCreator && isDraft && !editing && !readOnly && (isCorrectionDraft ? (
                        <span style={{ alignSelf: 'center', padding: '6px 10px', fontSize: 12.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8 }}>
                            {'정정 명세서는 작성 화면에서 발행해 주세요'}
                        </span>
                    ) : (
                        <button onClick={openPublishModal} disabled={publishing}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 20px', background: '#6366F1', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>
                            {publishing ? '발행 중...' : '발행하기'}
                        </button>
                    ))}
                    <button onClick={() => setShowMoreMenu(v => !v)} title={isCreator && isSettled && !readOnly ? (isOutsourceStatement ? '인쇄 · 정산 완료된 명세서는 삭제할 수 없습니다' : '인쇄 · 정산 완료된 명세서는 [고쳐서 다시 발행]으로 정정합니다') : '인쇄 · 삭제'}
                        style={{ padding: '8px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '800', color: '#475569' }}>
                        ⋯
                    </button>
                    {showMoreMenu && (
                        <div style={{ position: 'absolute', top: '110%', right: 0, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 140, overflow: 'hidden' }}>
                            {isCreator && !isDraft && !readOnly && (
                                <>
                                    {/* 비회원 명세서는 상단 주 버튼으로 올라감 — 메뉴에는 회원 치과용만 남긴다 */}
                                    {!isNonMemberStatement(statement) && (
                                        <button onClick={() => { setShowMoreMenu(false); handleShareLink(false); }} disabled={sharing}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#4338CA', textAlign: 'left', fontWeight: 600 }}>
                                            🔗 {sharing ? '링크 생성 중...' : '공유 링크 복사 (미가입 치과)'}
                                        </button>
                                    )}
                                    {statement.shareToken && (
                                        <button onClick={() => { setShowMoreMenu(false); handleShareLink(true); }} disabled={sharing}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#4338CA', textAlign: 'left' }}>
                                            {'↻ 링크 다시 발급'}
                                        </button>
                                    )}
                                </>
                            )}
                            {/* 편집 중에는 입력칸이 찍히므로 인쇄 막음 — 저장 뒤 인쇄 (2026-10-24 재점검 P1-5) */}
                            <button onClick={() => { setShowMoreMenu(false); handlePrint(); }} disabled={editing}
                                title={editing ? '수정을 저장(또는 취소)한 뒤 인쇄할 수 있습니다' : undefined}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: editing ? 'default' : 'pointer', fontSize: 13, color: editing ? '#94A3B8' : '#334155', textAlign: 'left' }}>
                                <Printer size={15} /> {editing ? '인쇄 (저장 후 가능)' : '인쇄'}
                            </button>
                            {canDelete && (
                                <button onClick={() => { setShowMoreMenu(false); handleDelete(); }} disabled={deleting}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#DC2626', textAlign: 'left' }}>
                                    <Trash2 size={15} /> 삭제
                                </button>
                            )}
                            {isCreator && isSettled && !readOnly && !isDeletedBySender && (
                                <div style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', borderTop: '1px solid #F1F5F9', lineHeight: 1.5 }}>
                                    {isOutsourceStatement
                                        ? '정산 완료된 명세서는 삭제할 수 없습니다.'
                                        : '정산 완료된 명세서는 [고쳐서 다시 발행]으로 정정합니다.'}
                                </div>
                            )}
                        </div>
                    )}
                    {editing && (
                        <>
                            <button onClick={() => { setEditing(false); fetchStatement(); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                                <X size={16} /> 취소
                            </button>
                            <button onClick={handleSave}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                <Save size={16} /> 저장
                            </button>
                        </>
                    )}
                    {/* 수신자 정산 검토 버튼 */}
                    {canReview && reviewing && (
                        <>
                            <button onClick={confirmStatement} disabled={submittingReview}
                                title={SETTLE_CARRY_OVER_NOTE}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                <CheckCircle size={16} /> {(statement.settledAt || (statement.settlementNotes || []).some(n => n.type === 'system' && (n.text || '').includes('정산'))) ? '재정산 확인' : '정산 확인'}
                            </button>
                            <button onClick={submitReview} disabled={submittingReview}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: '#F59E0B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                <AlertTriangle size={16} /> {submittingReview ? '전송 중...' : '수정 요청'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* 월 마감 진행줄 — 발행자(기공소)용 */}
            {isCreator && statement && (
                <div className="no-print" style={{ display: 'flex', alignItems: 'stretch', gap: 0, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '14px 6px', marginBottom: 18, flexWrap: 'wrap', rowGap: 10 }}>
                    {(() => {
                        const chip = (state, label, sub, action) => (
                            <div key={label} style={{ flex: '1 1 170px', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0 14px', borderLeft: '1px solid #F1F5F9' }}>
                                {/* state: done(초록) · warn(노랑) · alert(빨강 — 수정 요청 중) · todo(회색) */}
                                <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                                    background: state === 'done' ? '#D1FAE5' : state === 'warn' ? '#FEF3C7' : state === 'alert' ? '#FEE2E2' : '#F1F5F9',
                                    color: state === 'done' ? '#065F46' : state === 'warn' ? '#92400E' : state === 'alert' ? '#B91C1C' : '#94A3B8' }}>
                                    {state === 'done' ? '✓' : state === 'alert' ? '!' : '·'}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: state === 'todo' ? '#94A3B8' : '#0F172A' }}>{label}</div>
                                    <div style={{ fontSize: 11.5, color: '#64748B', lineHeight: 1.5 }}>{sub}</div>
                                    {action}
                                </div>
                            </div>
                        );
                        const issued = statement.status === 'confirmed';
                        const settled = statement.settlementStatus === 'settled';
                        const disputed = statement.settlementStatus === 'disputed';
                        // 임시저장(draft) 계산서는 아직 발행 전 — 발행(issued)만 완료로 표시
                        const taxPending = String(statement.taxInvoiceId || '').startsWith('pending:');   // 서버 원클릭 처리 중 잠금
                        // 멈춤 판단: 잠금 계산서의 마지막 갱신(updatedAt→createdAt)이 10분 넘음. 서버가 pending 시각을 따로 남기면 그것 우선
                        const pendingSince = msOf(statement.taxInvoicePendingAt) || msOf(linkedInvoice?.updatedAt) || msOf(linkedInvoice?.createdAt);
                        const taxStalled = taxPending && pendingSince > 0 && Date.now() - pendingSince > ONECLICK_STALL_MS;
                        const taxError = !!statement.taxInvoiceId && !taxPending && statement.taxInvoiceStatus === 'error';
                        const taxCancelled = !!statement.taxInvoiceId && CANCELED_LIKE_TAX.includes(statement.taxInvoiceStatus);
                        const hasTax = !!statement.taxInvoiceId && !taxPending && statement.taxInvoiceStatus === 'issued';
                        const taxLinked = !!statement.taxInvoiceId && !CANCELED_LIKE_TAX.includes(statement.taxInvoiceStatus);
                        // [계산서 바로 발행] 노출 (계약 §1): 회원 = 정산 완료 / 비회원 = 발행됨 + 서버 검증 링크 확인(shareConfirmedAt)
                        const oneClickBase = !readOnly && !isOutsourceStatement && !isDeletedBySender && issued && !taxLinked;
                        // 회원·비회원 최종 판정과 '확인 뒤 변경' 판정은 서버(users 존재·스냅샷 비교)가 함 — 화면은 정산 완료 또는 링크 확인이면 버튼을 보여 주고 서버 거절 문구를 그대로 안내 (10-24 최종검수 P2-1·2)
                        const oneClickReady = oneClickBase && (settled || !!statement.shareConfirmedAt);
                        const taxSub = taxError ? '전자발행 실패 — 계산서 상태 조회 후 다시 시도'
                            : taxStalled ? '처리가 멈췄습니다'
                            : hasTax
                            ? (linkedInvoice?.isElectronic
                                ? ((linkedInvoice.electronicMode === 'test' || linkedInvoice.popbillIsTest) ? '발행됨 · 테스트 발행 — 국세청 미전송' : `발행됨 · 국세청 전송${linkedInvoice.electronicStatus === 'error' ? ' 실패 — 계산서 화면 확인' : '됨'}`)
                                : '덴클 기록만 (국세청 미전송)')
                            : taxPending ? '발행 처리 중...'
                            : oneClickReady ? `${taxCancelled ? '이전 계산서 취소됨 · ' : ''}면세 · ${Math.round(Number(statement.totalAmount) || 0).toLocaleString()}원`
                            : taxCancelled ? '이전 계산서 취소됨 — 다시 발행할 수 있습니다'
                            : '명세서 금액 그대로 발행';
                        const smallBtn = { padding: '4px 10px', fontSize: 11.5, fontWeight: 700, border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' };
                        const fee = Math.round((statement.totalAmount || 0) * 0.045);
                        // 디자인외주 명세서(kind==='design_outsource')는 상대가 기공소이고, 매칭 수수료는 2026-08-15 무료화됨
                        const isOutsource = statement.kind === 'design_outsource';
                        const counterpartLabel = isOutsource ? '거래처(기공소)' : '치과';
                        // 2단계: 회원 치과 = 정산 완료(상태 이름) / 비회원 치과 = 링크 확인(정산 확인 아님). 링크 수정 요청이 있고 확인 전이면 빨간 '수정 요청 중(링크)' (P2-6)
                        const redSub = (t) => <span style={{ color: '#B91C1C', fontWeight: 700 }}>{t}</span>;
                        const step2 = isNonMemberClinic && !isOutsource
                            ? {
                                state: statement.shareConfirmedAt ? 'done' : hasLinkRevision ? 'alert' : issued ? 'warn' : 'todo',
                                label: '2. 링크 확인',
                                sub: hasLinkRevision ? redSub('수정 요청 중(링크) — 내용 확인')
                                    : statement.shareConfirmedAt ? `치과가 링크에서 확인 ${mdOf(statement.shareConfirmedAt)} (정산 확인은 아님)`
                                    : issued ? '카톡·문자로 보낸 링크에서 치과가 확인 (정산 확인은 아님)' : '발행 후 진행',
                            }
                            : {
                                state: settled ? 'done' : (disputed || hasLinkRevision) ? 'alert' : issued ? 'warn' : 'todo',
                                label: '2. 정산 완료',
                                sub: settled ? `${counterpartLabel}가 정산 확인함` : disputed ? redSub('수정 요청 중 — 내용 확인') : hasLinkRevision ? redSub('수정 요청 중(링크) — 내용 확인')
                                    : issued ? `${counterpartLabel} 확인 대기 중` : '발행 후 진행',
                            };
                        return (<>
                            {chip(issued ? 'done' : 'warn', '1. 발행됨', issued ? `${counterpartLabel}에 발송됨` : '발행 전 — 우측 상단 [발행]')}
                            {chip(step2.state, step2.label, step2.sub)}
                            {chip(hasTax ? 'done' : issued ? 'warn' : 'todo', '3. 계산서', taxSub,
                                readOnly ? null
                                : taxStalled ? (
                                    <button onClick={() => handleOneClickInvoice(true)} disabled={oneClicking}
                                        style={{ ...smallBtn, marginTop: 4, background: '#DC2626', opacity: oneClicking ? 0.6 : 1 }}>
                                        {oneClicking ? '처리 중...' : '다시 시도'}
                                    </button>
                                ) : taxError ? (
                                    <button onClick={() => navigate(`/tax-invoices/${statement.taxInvoiceId}`)}
                                        style={{ ...smallBtn, marginTop: 4, background: '#B45309' }}>
                                        {'계산서 화면으로 ▸'}
                                    </button>
                                ) : oneClickReady ? (
                                    <div style={{ marginTop: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <button onClick={() => handleOneClickInvoice(false)} disabled={oneClicking}
                                                style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 700, border: 'none', borderRadius: 6, background: '#059669', color: '#fff', cursor: oneClicking ? 'default' : 'pointer', opacity: oneClicking ? 0.6 : 1 }}>
                                                {oneClicking ? '발행 중...' : '계산서 바로 발행'}
                                            </button>
                                            <button onClick={() => navigate('/tax-invoices/create', { state: { fromStatement: { id: statementId, ...statement } } })}
                                                style={{ padding: 0, fontSize: 11, border: 'none', background: 'none', color: '#6366F1', cursor: 'pointer', textDecoration: 'underline' }}>
                                                {'과세·품목 수정은 ▸ 직접 작성'}
                                            </button>
                                        </div>
                                        {popbillLive !== true && (
                                            <div style={{ marginTop: 3, fontSize: 11, color: '#92400E' }}>{`${ONECLICK_LOCKED_NOTE}. 국세청 전송은 아직 열리지 않았습니다`}</div>
                                        )}
                                    </div>
                                ) : !hasTax && !taxPending && !taxLinked && issued && !readOnly ? (
                                    <button onClick={() => navigate('/tax-invoices/create', { state: { fromStatement: { id: statementId, ...statement } } })}
                                        style={{ marginTop: 4, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, border: 'none', borderRadius: 6, background: '#6366F1', color: '#fff', cursor: 'pointer' }}>
                                        세금계산서 발행하기
                                    </button>
                                ) : null)}
                            {/* 중개 수수료 전면 폐지 (2026-08-17 대표 확정, 약관 v6.0) — 매칭 여부와 무관하게 수수료 없음 */}
                            {chip('done', '4. 중개 수수료', '없음 — 전면 폐지 (2026-08-17)')}
                        </>);
                    })()}
                </div>
            )}

            {/* 명세서 본문 — 양식은 StatementSheet 단일 소스(미리보기 모달과 공유, 2026-10-06). 여기서는 편집·검토 UI만 slots로 끼운다 */}
            <StatementSheet
                statement={statement}
                items={editedItems}
                totals={totals}
                lastDisputeNote={lastDisputeNote}
                slots={{
                    beforeTable: (<>
                        {/* 수정 요청 모드 안내 */}
                        {reviewing && (
                            <div className="no-print" style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '12px 16px', background: '#FFFBEB', border: '1px solid #FDE68A',
                                borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#92400E',
                            }}>
                                <AlertTriangle size={18} />
                                <span>항목을 확인하세요. 수정이 필요한 항목은 체크하고 사유를 입력한 후 <strong>수정 요청</strong>을, 이상 없으면 <strong>{(statement.settledAt || (statement.settlementNotes || []).some(n => n.type === 'system' && (n.text || '').includes('정산'))) ? '재정산 확인' : '정산 확인'}</strong>을 눌러주세요.
                                    {/* 법무 고정 문구 (계약 §2) — 항상 표시 */}
                                    <br />{SETTLE_CARRY_OVER_NOTE}</span>
                            </div>
                        )}

                        {/* 편집 툴바 */}
                        {editing && (
                            <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                                <button onClick={openPriceListModal}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '6px 12px', background: '#F5F3FF', color: '#7C3AED',
                                        border: '1px solid #DDD6FE', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                                    }}>
                                    단가표 관리
                                    {priceList.length > 0 && (
                                        <span style={{ background: '#7C3AED', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '11px' }}>
                                            {priceList.length}
                                        </span>
                                    )}
                                </button>
                                <button onClick={applyPriceList}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '6px 12px', background: '#7C3AED', color: 'white',
                                        border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                    }}>
                                    단가표 적용
                                </button>
                                <button onClick={handleAddItem}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '6px 12px', background: '#F1F5F9', color: '#475569',
                                        border: '1px solid #E2E8F0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                                    }}>
                                    <Plus size={14} /> 수동 추가
                                </button>
                            </div>
                        )}

                        {/* 단가표 자동완성 (수정 시 치아정보 입력) */}
                        <datalist id="vts-price-options">
                            {priceList.map((p, i) => (
                                <option key={i} value={p.serviceName} />
                            ))}
                        </datalist>
                    </>),
                    tableProps: { onKeyDown: handleGridKeyDown },
                    headLead: reviewing ? <th className="no-print" style={{ width: '36px' }}></th> : null,
                    headTrail: editing ? <th className="no-print" style={{ width: '40px' }}></th> : null,
                    row: {
                        highlight: (index) => !!checkedItems[index],
                        showSourceBadges: !editing,
                        lead: (item, index) => reviewing ? (
                            <td className="no-print" style={{ padding: '6px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={!!checkedItems[index]}
                                    onChange={() => toggleCheckItem(index)}
                                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#F59E0B' }}
                                />
                            </td>
                        ) : null,
                        patient: (item, index) => editing ? (
                            // 편집 입력칸은 인쇄 제외(no-print) — 편집 중엔 [인쇄] 자체를 막아 두었고, 이건 이중 방어 (P1-5)
                            <input className="no-print" data-nav="patient" value={item.patientName || ''} onChange={(e) => {
                                const updated = [...editedItems];
                                updated[index].patientName = e.target.value;
                                setEditedItems(updated);
                            }} style={{ border: '1px solid #E2E8F0', borderRadius: '4px', padding: '4px 8px', width: '80px', fontSize: '13px' }} />
                        ) : undefined,
                        tooth: (item, index) => editing ? (
                            <input className="no-print" data-nav="tooth" list="vts-price-options" value={item.toothInfo || ''} onChange={(e) => handleToothInfoChange(index, e.target.value)}
                                style={{ border: '1px solid #E2E8F0', borderRadius: '4px', padding: '4px 8px', width: '100%', fontSize: '13px', boxSizing: 'border-box' }}
                                placeholder="#11,21 크라운 (단가표 자동완성)" />
                        ) : undefined,
                        count: (item, index) => editing ? (
                            <input className="no-print" data-nav="count" type="number" value={item.toothCount || 0} onChange={(e) => handleToothCountChange(index, e.target.value)}
                                style={{ border: '1px solid #E2E8F0', borderRadius: '4px', padding: '4px 8px', width: '50px', textAlign: 'center', fontSize: '13px' }} min="0" />
                        ) : undefined,
                        unitPrice: (item, index) => editing ? (
                            <input className="no-print" data-nav="price" type="number" value={item.pricePerTooth || item.unitPrice || 0} onChange={(e) => handlePricePerToothChange(index, e.target.value)}
                                style={{ border: '1px solid #E2E8F0', borderRadius: '4px', padding: '4px 8px', width: '80px', textAlign: 'right', fontSize: '13px' }} min="0" />
                        ) : undefined,
                        trail: (item, index) => editing ? (
                            <td className="no-print" style={{ padding: '8px' }}>
                                <button onClick={() => removeItem(index)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px' }}>
                                    <Trash2 size={16} />
                                </button>
                            </td>
                        ) : null,
                        // 수정 사유 입력 (수신자 검토 모드)
                        after: (item, index) => (reviewing && checkedItems[index]) ? (
                            <tr className="no-print" style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
                                <td></td>
                                <td colSpan={editing ? 8 : 7} style={{ padding: '4px 8px 10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <MessageSquare size={14} color="#92400E" style={{ flexShrink: 0 }} />
                                        <input
                                            type="text"
                                            value={reviewReasons[index] || ''}
                                            onChange={(e) => setReviewReasons(prev => ({ ...prev, [index]: e.target.value }))}
                                            placeholder="수정 사유를 입력하세요 (예: 단가 오류, 개수 불일치 등)"
                                            style={{
                                                flex: 1, padding: '6px 10px', border: '1px solid #FDE68A',
                                                borderRadius: '6px', fontSize: '13px', background: '#fff', outline: 'none',
                                            }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ) : null,
                    },
                    showDisputeRows: !reviewing,
                    // 할인/메모 (편집 모드)
                    afterTable: editing ? (
                        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748B', marginBottom: '4px' }}>할인</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input type="number" value={editedDiscount} onChange={(e) => setEditedDiscount(e.target.value)}
                                        style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px' }} />
                                    <select value={editedDiscountType} onChange={(e) => setEditedDiscountType(e.target.value)}
                                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px' }}>
                                        <option value="amount">원</option>
                                        <option value="percent">%</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748B', marginBottom: '4px' }}>메모</label>
                                <textarea value={editedNotes} onChange={(e) => setEditedNotes(e.target.value)}
                                    rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} />
                            </div>
                        </div>
                    ) : null,
                    hideNotes: editing,
                    // 수정 요청 모드: 전체 메모
                    afterNotes: reviewing ? (
                        <div className="no-print" style={{ marginTop: '20px' }}>
                            <label style={{ display: 'block', fontSize: '13px', color: '#64748B', marginBottom: '4px', fontWeight: '600' }}>전체 메모 (선택)</label>
                            <textarea
                                value={reviewGeneralNote}
                                onChange={(e) => setReviewGeneralNote(e.target.value)}
                                rows={2}
                                placeholder="추가로 전달할 내용이 있으면 입력하세요"
                                style={{
                                    width: '100%', padding: '8px 12px', borderRadius: '8px',
                                    border: '1px solid #E2E8F0', fontSize: '14px', resize: 'vertical',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                    ) : null,
                    hideDisputeHistory: reviewing,
                }}
            />

            {/* 발행 확인 창 (2026-10-24 R-1) — window.confirm 에는 체크칸을 넣을 수 없어 작은 창으로 */}
            {publishModal && (
                <div className="no-print" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: '22px 22px 18px', width: 440, maxWidth: '100%', boxSizing: 'border-box' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', marginBottom: 6 }}>{'거래명세서를 발행하시겠습니까?'}</div>
                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>{'거래처에게 알림이 발송됩니다.'}</div>
                        {publishModal.loading && (
                            <div style={{ fontSize: 12.5, color: '#94A3B8', marginBottom: 12 }}>{'전월 미수 확인 중...'}</div>
                        )}
                        {/* 전월 미수 — 기본 꺼짐, 0원 이하면 숨김. 켜면 발행 때 다시 계산해 합계 아래 별도 상자로 고정 */}
                        {carryOverAllowed && (
                            <div style={{ padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#F8FAFC', fontSize: 13, color: '#334155', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700 }}>{`전월 미수 ${publishModal.preview.amount.toLocaleString()}원`}</span>
                                    <span style={{ fontSize: 11.5, color: '#94A3B8' }}>{`${publishModal.preview.asOf} 현재 · 입금 기록 기준`}</span>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 'auto', fontWeight: 600 }}>
                                        <input type="checkbox" checked={includeCarryOver} onChange={(e) => setIncludeCarryOver(e.target.checked)}
                                            style={{ width: 15, height: 15, accentColor: '#6366F1' }} />
                                        {'명세서에 적기'}
                                    </label>
                                </div>
                                {includeCarryOver && !isMemberPartnerKey(viewPartnerKey) && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: '#B45309', fontWeight: 600 }}>
                                        {'덴클 회원이 아닌 치과입니다 — 입금을 빠짐없이 기록했는지 확인한 뒤 적어 주세요. 이미 낸 치과에 미수로 찍힐 수 있습니다.'}
                                    </div>
                                )}
                                {includeCarryOver && (
                                    <div style={{ marginTop: 4, fontSize: 11.5, color: '#64748B' }}>
                                        {'합계에는 더하지 않고, 합계 아래 별도 상자로 적습니다. 금액은 발행할 때 다시 계산합니다.'}
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setPublishModal(null)} disabled={publishing}
                                style={{ padding: '9px 18px', background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13.5 }}>
                                {'취소'}
                            </button>
                            <button onClick={handlePublish} disabled={publishing || publishModal.loading}
                                style={{ padding: '9px 20px', background: '#6366F1', color: '#fff', border: 'none', borderRadius: 8, cursor: (publishing || publishModal.loading) ? 'default' : 'pointer', fontWeight: 700, fontSize: 13.5, opacity: (publishing || publishModal.loading) ? 0.6 : 1 }}>
                                {publishing ? '발행 중...' : '발행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 단가표 관리 모달 */}
            {showPriceListModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: 'white', borderRadius: '16px', padding: '28px',
                        width: '520px', maxHeight: '80vh', overflowY: 'auto',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                                단가표 관리
                                <span style={{ fontSize: '14px', fontWeight: '400', color: '#64748B', marginLeft: '8px' }}>
                                    {isCreator ? statement?.toUserName : statement?.fromUserName}
                                </span>
                            </h3>
                            <button onClick={() => setShowPriceListModal(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#94A3B8' }}>
                                ×
                            </button>
                        </div>

                        {/* 프리셋 칩 */}
                        <div style={{ marginBottom: '16px', padding: '12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569' }}>보철물</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                    {prosthesisPresets.map(name => (
                                        <button key={name} onClick={() => handleAddPricePreset(name)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '3px',
                                                padding: '4px 10px', borderRadius: '16px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                                                background: editingPriceList.some(i => i.serviceName === name) ? '#E2E8F0' : '#EEF2FF',
                                                border: `1px solid ${editingPriceList.some(i => i.serviceName === name) ? '#CBD5E1' : '#C7D2FE'}`,
                                                color: editingPriceList.some(i => i.serviceName === name) ? '#94A3B8' : '#4F46E5',
                                            }}>
                                            <Plus size={10} /> {name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569' }}>임플란트</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                    {implantPresets.map(name => (
                                        <button key={name} onClick={() => handleAddPricePreset(name)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '3px',
                                                padding: '4px 10px', borderRadius: '16px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                                                background: editingPriceList.some(i => i.serviceName === name) ? '#E2E8F0' : '#EFF6FF',
                                                border: `1px solid ${editingPriceList.some(i => i.serviceName === name) ? '#CBD5E1' : '#93C5FD'}`,
                                                color: editingPriceList.some(i => i.serviceName === name) ? '#94A3B8' : '#1D4ED8',
                                            }}>
                                            <Plus size={10} /> {name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', color: '#64748B', fontSize: '13px' }}>서비스명</th>
                                    <th style={{ padding: '8px', textAlign: 'right', color: '#64748B', fontSize: '13px', width: '120px' }}>단가 (원)</th>
                                    <th style={{ padding: '8px', width: '40px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {editingPriceList.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '6px 8px' }}>
                                            <input value={item.serviceName}
                                                onChange={(e) => {
                                                    const updated = [...editingPriceList];
                                                    updated[idx].serviceName = e.target.value;
                                                    setEditingPriceList(updated);
                                                }}
                                                placeholder="예: 지르코니아 크라운"
                                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', boxSizing: 'border-box' }} />
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            <input type="number" value={item.unitPrice}
                                                onChange={(e) => {
                                                    const updated = [...editingPriceList];
                                                    updated[idx].unitPrice = Number(e.target.value) || 0;
                                                    setEditingPriceList(updated);
                                                }}
                                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', textAlign: 'right', boxSizing: 'border-box' }} />
                                        </td>
                                        <td style={{ padding: '6px 4px' }}>
                                            <button onClick={() => setEditingPriceList(editingPriceList.filter((_, i) => i !== idx))}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px' }}>
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <button onClick={() => setEditingPriceList([...editingPriceList, { serviceName: '', unitPrice: 0 }])}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '4px', width: '100%',
                                padding: '10px', background: '#F8FAFC', color: '#64748B',
                                border: '1px dashed #CBD5E1', borderRadius: '8px', cursor: 'pointer',
                                fontSize: '13px', justifyContent: 'center', marginBottom: '20px',
                            }}>
                            <Plus size={14} /> 항목 추가
                        </button>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button onClick={() => setShowPriceListModal(false)}
                                style={{ padding: '10px 20px', background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                                취소
                            </button>
                            <button onClick={savePriceList}
                                style={{ padding: '10px 20px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ViewTransactionStatement;
